import React, { useEffect, useRef, useState } from 'react';

import { AspectRatio, ClipFraming, ProjectAsset, VideoClip } from '../../types/editor';
import { dimensionsForAspect } from './migrateTimeline';

/* Live WYSIWYG preview inside the ClipInspector modal. Shows the
 * selected clip's frame composited at the current aspect ratio with
 * the user's framing (zoom + pan) applied — same math the canvas
 * preview at the top of the editor uses (PreviewCanvas.tsx), but
 * scoped to one clip + one frame so it can re-render cheaply while
 * the user moves the sliders.
 *
 * For video clips we seek to the clip's trimIn (i.e. the first
 * visible frame in the final render) so the thumbnail reflects what
 * the user will actually see at the top of the clip.
 *
 * Audio clips have nothing visual to show — the inspector skips
 * rendering this component for them. */

interface ClipFrameThumbnailProps {
  clip: VideoClip;
  asset: ProjectAsset | undefined;
  aspect: AspectRatio;
  framing: ClipFraming;
}

const THUMB_MAX_HEIGHT = 200;
const THUMB_MAX_WIDTH = 320;

function thumbDims(aspect: AspectRatio): { width: number; height: number } {
  const { width, height } = dimensionsForAspect(aspect);
  const ratio = width / height;
  if (ratio > THUMB_MAX_WIDTH / THUMB_MAX_HEIGHT) {
    const w = THUMB_MAX_WIDTH;
    return { width: w, height: Math.round(w / ratio) };
  }
  const h = THUMB_MAX_HEIGHT;
  return { width: Math.round(h * ratio), height: h };
}

export default function ClipFrameThumbnail({
  clip, asset, aspect, framing,
}: ClipFrameThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceRef = useRef<HTMLImageElement | HTMLVideoElement | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  /* Load the source whenever the clip or asset changes. crossOrigin
   * is intentionally NOT set — the dev media isn't served with CORS
   * headers, and matching PreviewCanvas's behavior keeps everything
   * same-origin / un-tainted via the Caddy /media proxy. */
  useEffect(() => {
    setReady(false);
    setLoadError(false);
    sourceRef.current = null;
    if (!asset || !asset.file_url) return undefined;

    if (asset.kind === 'image') {
      const img = new window.Image();
      img.onload = () => { sourceRef.current = img; setReady(true); };
      img.onerror = () => setLoadError(true);
      img.src = asset.file_url;
      return () => { img.onload = null; img.onerror = null; };
    }

    if (asset.kind === 'video') {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      const onLoaded = (): void => {
        try { video.currentTime = clip.trimIn; }
        catch { sourceRef.current = video; setReady(true); }
      };
      const onSeeked = (): void => { sourceRef.current = video; setReady(true); };
      const onErr = (): void => setLoadError(true);
      video.addEventListener('loadeddata', onLoaded);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onErr);
      video.src = asset.file_url;
      return () => {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onErr);
      };
    }
    return undefined;
  }, [asset, clip.trimIn]);

  const { width: W, height: H } = thumbDims(aspect);

  /* Redraw on every framing tweak. Cheap: it's one drawImage onto a
   * ~64k-pixel canvas. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = W;
    canvas.height = H;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);

    if (!ready) return;
    const source = sourceRef.current;
    if (!source) return;

    // Source dimensions: prefer asset metadata (more reliable on first
    // paint), fall back to the loaded element's intrinsic size.
    const v = source as HTMLVideoElement & HTMLImageElement;
    const sw = asset?.width || v.videoWidth || v.naturalWidth || W;
    const sh = asset?.height || v.videoHeight || v.naturalHeight || H;

    // contain-fit, then apply user framing (scale + pan). Mirrors the
    // identical block in PreviewCanvas so the modal thumb matches the
    // main preview at the same playhead time.
    const fitScale = Math.min(W / sw, H / sh);
    const fitW = sw * fitScale;
    const fitH = sh * fitScale;
    const dw = fitW * framing.scale;
    const dh = fitH * framing.scale;
    const dx = (W - dw) / 2 + framing.offsetX * W / 2;
    const dy = (H - dh) / 2 + framing.offsetY * H / 2;
    try { ctx.drawImage(source, dx, dy, dw, dh); }
    catch { /* element not yet ready for drawImage on this canvas */ }
  }, [ready, framing.scale, framing.offsetX, framing.offsetY, W, H, asset]);

  return (
    <div className="inspector-thumb" style={{ width: W, height: H }}>
      <canvas ref={canvasRef} className="inspector-thumb-canvas" />
      {loadError && (
        <div className="inspector-thumb-error muted">Preview unavailable</div>
      )}
    </div>
  );
}
